import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, hadAuthSessionHint } from '../context/AuthContext';
import { ProjectSetupData } from '../components/ProjectSettings';
import { DisplayMode } from '../models/project';
import { getProfile } from '../services/profile';
import LandingHeader from '../components/Landing/LandingHeader';
import HeroSection from '../components/Landing/HeroSection';
import StepsSection from '../components/Landing/StepsSection';
import SayItSection from '../components/Landing/SayItSection';
import StoresSection from '../components/Landing/StoresSection';
import BuiltInSection from '../components/Landing/BuiltInSection';
import AgentsSection from '../components/Landing/AgentsSection';
import FeaturedGamesSection from '../components/Landing/FeaturedGamesSection';
import { CtaSection, CommunitySection, FaqSection, LandingFooter } from '../components/Landing/ClosingSections';

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation(['landing', 'common']);
  const [pendingProject, setPendingProject] = useState<ProjectSetupData | null>(null);
  // Captured once on first render: if this browser had a session, show a loader during the
  // boot-time /auth/me check instead of flashing the marketing page before redirecting.
  const [expectAuthRedirect] = useState(() => hadAuthSessionHint());
  const [pitch, setPitch] = useState('');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('MobilePortrait');

  useEffect(() => {
    const saved = localStorage.getItem('pendingProject');
    if (saved) {
      const parsed: ProjectSetupData = JSON.parse(saved);
      setPendingProject(parsed);
      if (parsed.description) setPitch(parsed.description);
      if (parsed.displayMode) setDisplayMode(parsed.displayMode);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      getProfile().catch(() => {});
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !pendingProject) {
      navigate('/profile');
    }
  }, [isAuthenticated, navigate, pendingProject]);

  // A returning signed-in user (or one mid-redirect): show a loader rather than the marketing
  // page while the session resolves, so they don't see the page flash before landing on /profile.
  if ((isLoading && expectAuthRedirect) || (isAuthenticated && !pendingProject)) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <div className="muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
            <circle cx="12" cy="12" r="9" opacity="0.25" />
            <path d="M21 12a9 9 0 0 0-9-9" />
          </svg>
          {t('common:loading', 'Loading…')}
        </div>
      </div>
    );
  }

  const onStart = () => {
    const data: ProjectSetupData = {
      name: pendingProject?.name || 'Untitled',
      description: pitch.trim(),
      type: '2D',
      displayMode,
      providedAssets: [],
    };
    localStorage.setItem('pendingProject', JSON.stringify(data));
    navigate(isAuthenticated ? '/project-setup' : '/login');
  };

  return (
    <div style={{ minHeight: '100svh', background: 'var(--bg)', color: 'var(--text)' }}>
      <LandingHeader onStart={onStart} />
      <main>
        <HeroSection
          pitch={pitch}
          onPitchChange={setPitch}
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          onCreate={onStart}
        />
        <StepsSection />
        <SayItSection />
        <StoresSection />
        <BuiltInSection />
        <AgentsSection />
        <FeaturedGamesSection />
        <CtaSection onStart={onStart} />
        <CommunitySection />
        <FaqSection />
      </main>
      <LandingFooter />
    </div>
  );
}
